/**
 * PWR web job — WHAT DAP writes and HOW Requested vs Cancelled is decided.
 *
 * WHEN each step runs → usePwrJourneySteps.ts (SiteSelection, Layout, Contact).
 * Flag merge (all workflows): mirrors sites.api BuildPWRFlags — website gate + Global AND Local
 * protocol when Local Protocol map has entry (page-props context.pwrLocalProtocols). CDP for site PR and inflare.
 *
 * READ THIS FILE TOP TO BOTTOM:
 *   §1 API writes   — postPwrInitiatedRecord, postPwrCompleteJob
 *   §2 Routing      — evaluatePwrRouting → Requested | Cancelled
 *   §3 Internal     — audit payload, skip rules, protocol list (private)
 *
 * CDP guards and journey timing (isEnrollmentDnq on outcome/contact paths, isNoSiteReferralOutcome, WF gates) live in §3;
 * usePwrJourneySteps imports those — one copy, not duplicated in the hook.
 */

import { JOURNEYS } from '../../constants';
import { post } from '../utils/axiosFetcher';
import { getOrCreateEnrollmentId } from '../utils/enrollmentUtil';
import {
  getPwrFlags,
  getSitePreRecruitmentFromCdp,
  getSitePrFromCdp,
  PWRFlagsResponse,
  PwrLocalProtocolSummary,
} from './pwrFlagResolution';

export const PWR_SOURCE_DAP = 'DAP';

/** Shared codebase (DAP + Avacare) — PWR flows run only when NEXT_PUBLIC_PROJECT_TYPE=DAP. */
export const isDapProject = (): boolean => process.env.NEXT_PUBLIC_PROJECT_TYPE === 'DAP';

export type PwrEvaluationContext = 'siteSelection' | 'contactSubmit' | 'outcomePage';

// ═══════════════════════════════════════════════════════════════════════════════
// §1  API WRITES — Initiated | Requested | Cancelled (+ flags audit)
// ═══════════════════════════════════════════════════════════════════════════════

export interface PwrInitiatedJobParams {
  cdpData: any;
  siteSettings: any;
  localProtocols: Record<string, PwrLocalProtocolSummary>;
  /** screenerOutput.pSec when cdpData.qualifiedProtocol is empty */
  screenerQualifiedProtocol?: string | string[] | null;
  /** screener CDP / survey — inflare is not on site-selection or contact experiences */
  screenerInflare?: string | null;
}

export interface PwrCompleteJobParams {
  /** Enrollment / step CDP snapshot used for FLA, qualifiedProtocol, DNQ (outcome/contact). */
  cdpData: any;
  siteSettings: any;
  localProtocols: Record<string, PwrLocalProtocolSummary>;
  journeyType?: string | null;
  evaluationContext?: PwrEvaluationContext;
  /** screenerOutput.pSec when cdpData.qualifiedProtocol is empty */
  screenerQualifiedProtocol?: string | string[] | null;
  /** screener CDP / survey — inflare is not on site-selection or contact experiences */
  screenerInflare?: string | null;
  /**
   * Confirm path only — selectedPlace.siteInfo (GetSites row).
   * Site PR comes from here; do not wait for CDP next-step sitesData.
   */
  selectedSiteInfo?: { enableSitePreRecruitment?: boolean } | null;
}

const completeJobDedupeKeys = new Set<string>();

/** Flare answer lives on screener experience; Alchemer key is FLA (legacy CDP key: inflare). */
function resolveInflareValue(
  cdpEnrollment?: any,
  screenerInflare?: string | null
): string | undefined {
  const fromEnrollment = cdpEnrollment?.FLA ?? cdpEnrollment?.inflare;
  if (fromEnrollment != null && String(fromEnrollment).trim() !== '') {
    return String(fromEnrollment);
  }
  if (screenerInflare != null && String(screenerInflare).trim() !== '') {
    return String(screenerInflare);
  }
  return undefined;
}

/** WF-2/3 site-selection mount → processStatus Initiated. */
export async function postPwrInitiatedRecord(
  initiatedJobParams: PwrInitiatedJobParams
): Promise<boolean> {
  const {
    cdpData: cdpEnrollment,
    siteSettings,
    localProtocols,
    screenerQualifiedProtocol,
    screenerInflare,
  } = initiatedJobParams;

  if (!isDapProject()) {
    return false;
  }

  const enrollmentId = getOrCreateEnrollmentId();
  if (!enrollmentId) {
    return false;
  }

  const inflareValue = resolveInflareValue(cdpEnrollment, screenerInflare);
  const qualifiedProtocols = resolveQualifiedProtocolsForPwr(
    cdpEnrollment?.qualifiedProtocol ?? null,
    screenerQualifiedProtocol
  );

  // Initiated — Sitecore + Local Protocol only; no site selected yet (isReferralPathWithSite false).
  const { protocolFlags } = evaluatePwrRouting({
    qualifiedProtocols,
    siteSettings,
    localProtocols,
    inflareValue,
    isReferralPathWithSite: false,
  });

  const flagsAuditPayload = buildPwrFlagsAuditPayload(protocolFlags, inflareValue, false);

  try {
    await post('/PWR/AddOrUpdatePwrWebJobAndFlagsAuditRecords', [
      {
        enrollmentId,
        processStatus: 'Initiated',
        source: PWR_SOURCE_DAP,
        pwrFlagsAudit: flagsAuditPayload,
      },
    ]);
    return true;
  } catch (_error) {
    return false;
  }
}

/** Site-selection confirm, contact submit (WF-1), or outcome page → Requested | Cancelled. */
export async function postPwrCompleteJob(
  completeJobParams: PwrCompleteJobParams
): Promise<boolean> {
  const {
    cdpData,
    siteSettings,
    localProtocols,
    journeyType,
    evaluationContext = 'siteSelection',
    screenerQualifiedProtocol,
    screenerInflare,
    selectedSiteInfo,
  } = completeJobParams;

  // Confirm Completes before CDP next-step — site is already chosen.
  const isReferralPathWithSite =
    evaluationContext === 'siteSelection'
      ? true
      : cdpData?.nextStepUrl?.includes('siteselectionsuccess') === true;

  if (!isDapProject() || (!isReferralPathWithSite && isEnrollmentDnq(cdpData))) {
    return false;
  }

  const enrollmentId = getOrCreateEnrollmentId();
  if (!enrollmentId) {
    return false;
  }

  const dedupeKey = `${enrollmentId}|${evaluationContext}`;
  if (completeJobDedupeKeys.has(dedupeKey)) {
    return false;
  }
  completeJobDedupeKeys.add(dedupeKey);

  const patientReferralStatus = cdpData?.patientReferralStatus;

  const { isSendToPWR, protocolFlags, inflareValue } = resolvePwrCompleteRouting({
    cdpData,
    siteSettings,
    localProtocols,
    screenerQualifiedProtocol,
    screenerInflare,
    isReferralPathWithSite,
    selectedSiteInfo,
  });

  const skippedByAllFlagsOff = shouldSkipPwrApiWhenAllFlagsOffOnNonReferral({
    journeyType: journeyType ?? cdpData?.journeyType,
    evaluationContext,
    patientReferralStatus,
    isSendToPWR,
    isReferralPathWithSite,
  });

  if (skippedByAllFlagsOff) {
    completeJobDedupeKeys.delete(dedupeKey);
    return false;
  }

  const processStatus = isSendToPWR ? 'Requested' : 'Cancelled';
  const flagsAuditPayload = buildPwrFlagsAuditPayload(
    protocolFlags,
    inflareValue,
    isReferralPathWithSite
  );
  const pwrWebJobRecord: any = {
    enrollmentId,
    processStatus,
    source: PWR_SOURCE_DAP,
    pwrFlagsAudit: flagsAuditPayload,
  };

  try {
    await post('/PWR/AddOrUpdatePwrWebJobAndFlagsAuditRecords', [pwrWebJobRecord]);
    return true;
  } catch (_error) {
    completeJobDedupeKeys.delete(dedupeKey);
    return false;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// §2  ROUTING — Requested (send to PWR) vs Cancelled (RH / site allocation)
// ═══════════════════════════════════════════════════════════════════════════════

export interface PwrRoutingFlags {
  enableFlareWatch: boolean;
  enableSecondaryScreener: boolean;
  isPreRecruitmentOn: boolean;
  inflareValue?: string | null;
  isReferralPathWithSite: boolean;
}

export interface EvaluatePwrRoutingOptions {
  qualifiedProtocols: string[];
  siteSettings: any;
  localProtocols: Record<string, PwrLocalProtocolSummary>;
  /** CDP GetSites enableSitePreRecruitment — boolean; pass only when isReferralPathWithSite */
  siteEnablePreRecruitment?: boolean;
  inflareValue?: string | null;
  isReferralPathWithSite: boolean;
}

export interface ResolvePwrCompleteRoutingParams {
  cdpData: any;
  siteSettings: any;
  localProtocols: Record<string, PwrLocalProtocolSummary>;
  screenerQualifiedProtocol?: string | string[] | null;
  screenerInflare?: string | null;
  isReferralPathWithSite: boolean;
  selectedSiteInfo?: { enableSitePreRecruitment?: boolean } | null;
}

/**
 * Same UC routing as postPwrCompleteJob — used by outcome UpdatePatientOutcomeDetails (SU gate)
 * and by Complete (Requested | Cancelled).
 */
export function resolvePwrCompleteRouting(params: ResolvePwrCompleteRoutingParams): {
  isSendToPWR: boolean;
  protocolFlags: PWRFlagsResponse | null;
  inflareValue: string | undefined;
} {
  const inflareValue = resolveInflareValue(params.cdpData, params.screenerInflare);
  const qualifiedProtocols = resolveQualifiedProtocolsForPwr(
    params.cdpData?.qualifiedProtocol ?? null,
    params.screenerQualifiedProtocol
  );
  // Confirm: selectedPlace.siteInfo; contact/outcome-with-site: CDP sitesData[0].
  const siteEnablePreRecruitment = params.isReferralPathWithSite
    ? params.selectedSiteInfo != null
      ? getSitePrFromCdp(params.selectedSiteInfo)
      : getSitePreRecruitmentFromCdp(params.cdpData)
    : undefined;

  const { isSendToPWR, protocolFlags } = evaluatePwrRouting({
    qualifiedProtocols,
    siteSettings: params.siteSettings,
    localProtocols: params.localProtocols,
    siteEnablePreRecruitment,
    inflareValue,
    isReferralPathWithSite: params.isReferralPathWithSite,
  });

  return { isSendToPWR, protocolFlags, inflareValue };
}

/** PR for routing: site + Local Protocol on NSA/NSC; site PR required on referral path with site. */
export function isPreRecruitmentOnForRouting(
  protocolFlags: PWRFlagsResponse | null,
  isReferralPathWithSite: boolean
): boolean {
  if (!protocolFlags?.enableProtocolAndEprPreRecruitment) {
    return false;
  }
  if (isReferralPathWithSite) {
    return protocolFlags.enableSitePreRecruitment;
  }
  return true;
}

/** UC-04–06: should this enrollment go to PWR (vs RH / Site Allocation)? */
export function getIsSendToPWR(routingFlags: PwrRoutingFlags): boolean {
  const {
    enableFlareWatch: isFlareWatchOn,
    enableSecondaryScreener: isSecondaryScreenerOn,
    isPreRecruitmentOn,
    inflareValue,
    isReferralPathWithSite,
  } = routingFlags;

  const isUserInFlareForEnrollment = isUserInFlare(inflareValue);
  const isSecondaryScreenerOrPreRecruitmentOn = isSecondaryScreenerOn || isPreRecruitmentOn;

  if (
    isReferralPathWithSite &&
    isUserInFlareForEnrollment &&
    (isFlareWatchOn || isSecondaryScreenerOrPreRecruitmentOn)
  ) {
    return false;
  }
  if (!isReferralPathWithSite && isUserInFlareForEnrollment && isFlareWatchOn) {
    return true;
  }
  if (isFlareWatchOn && !isUserInFlareForEnrollment) {
    return true;
  }
  if (!isFlareWatchOn && isSecondaryScreenerOrPreRecruitmentOn) {
    return true;
  }

  return false;
}

export function evaluatePwrRouting(routingOptions: EvaluatePwrRoutingOptions): {
  isSendToPWR: boolean;
  protocolFlags: PWRFlagsResponse | null;
} {
  if (!routingOptions.qualifiedProtocols?.length) {
    return { isSendToPWR: false, protocolFlags: null };
  }

  let resolvedProtocolFlags: PWRFlagsResponse | null = null;

  for (const qualifiedProtocol of routingOptions.qualifiedProtocols) {
    const flagsForQualifiedProtocol = getPwrFlags(
      qualifiedProtocol,
      routingOptions.siteSettings,
      routingOptions.localProtocols,
      routingOptions.siteEnablePreRecruitment
    );
    if (!flagsForQualifiedProtocol) {
      continue;
    }
    if (!resolvedProtocolFlags) {
      resolvedProtocolFlags = flagsForQualifiedProtocol;
    }

    const isPreRecruitmentOn = isPreRecruitmentOnForRouting(
      flagsForQualifiedProtocol,
      routingOptions.isReferralPathWithSite
    );

    if (
      getIsSendToPWR({
        enableFlareWatch: flagsForQualifiedProtocol.enableFlareWatch,
        enableSecondaryScreener: flagsForQualifiedProtocol.enableSecondaryScreener,
        isPreRecruitmentOn,
        inflareValue: routingOptions.inflareValue,
        isReferralPathWithSite: routingOptions.isReferralPathWithSite,
      })
    ) {
      return { isSendToPWR: true, protocolFlags: flagsForQualifiedProtocol };
    }
  }

  if (resolvedProtocolFlags) {
    const isPreRecruitmentOn = isPreRecruitmentOnForRouting(
      resolvedProtocolFlags,
      routingOptions.isReferralPathWithSite
    );
    return {
      isSendToPWR: getIsSendToPWR({
        enableFlareWatch: resolvedProtocolFlags.enableFlareWatch,
        enableSecondaryScreener: resolvedProtocolFlags.enableSecondaryScreener,
        isPreRecruitmentOn,
        inflareValue: routingOptions.inflareValue,
        isReferralPathWithSite: routingOptions.isReferralPathWithSite,
      }),
      protocolFlags: resolvedProtocolFlags,
    };
  }

  return { isSendToPWR: false, protocolFlags: null };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3  INTERNAL — helpers for §1 and §2 (CDP guards exported for hook only)
// ═══════════════════════════════════════════════════════════════════════════════

/** DNQ — block PWR Complete writes. Required on outcome pages (DNQ shares isOutcomePage with NSA/NSC) and contact submit; not used for Initiated (WF-2/3 screener runs before site selection). */
export function isEnrollmentDnq(cdpEnrollment: any): boolean {
  return cdpEnrollment?.patientReferralStatus === 'DNQ' || cdpEnrollment?.isDnq === true;
}

/** Same CDP values as useUpdateUCPMJobRecord — hook uses for WF-2/3 timing. */
export function isNoSiteReferralOutcome(patientReferralStatus?: string | null): boolean {
  if (!patientReferralStatus) {
    return false;
  }
  return (
    ['No-site-available', 'Site-not-found', 'No-site-convenient', 'Site-not-convenient'].includes(
      patientReferralStatus
    ) || patientReferralStatus.includes('No-site-available-Max-Capacity')
  );
}

export function shouldDeferPwrCompleteUntilContact(journeyType?: string | null): boolean {
  return journeyType === JOURNEYS.JOURNEY_01;
}

export function shouldSubmitPwrInitiatedOnSiteSelectionMount(journeyType?: string | null): boolean {
  return journeyType === JOURNEYS.JOURNEY_02 || journeyType === JOURNEYS.JOURNEY_03;
}

/** WF-2/3 + no-site referral — defer Complete at site selection; run on outcome page. */
export function isWf23NoSiteReferralOutcome(
  journeyType?: string | null,
  patientReferralStatus?: string | null
): boolean {
  if (journeyType !== JOURNEYS.JOURNEY_02 && journeyType !== JOURNEYS.JOURNEY_03) {
    return false;
  }
  return isNoSiteReferralOutcome(patientReferralStatus);
}

const IN_FLARE_ANSWER_VALUES = new Set(['1', 'true', 'yes']);

function isUserInFlare(inflareAnswer?: string | null): boolean {
  if (!inflareAnswer?.trim()) {
    return false;
  }
  return IN_FLARE_ANSWER_VALUES.has(inflareAnswer.trim().toLowerCase());
}

function getQualifiedProtocols(cdpQualifiedProtocol?: string | string[] | null): string[] {
  const splitProtocolTokens = (value: string | null | undefined): string[] => {
    if (value == null || String(value).trim() === '') {
      return [];
    }
    return String(value)
      .split(',')
      .map((protocol) => protocol.trim())
      .filter((protocol) => protocol !== '');
  };

  if (Array.isArray(cdpQualifiedProtocol)) {
    return cdpQualifiedProtocol.flatMap((protocol) =>
      splitProtocolTokens(protocol == null ? '' : String(protocol))
    );
  }
  return splitProtocolTokens(cdpQualifiedProtocol == null ? '' : String(cdpQualifiedProtocol));
}

function resolveQualifiedProtocolsForPwr(
  cdpQualified?: string | string[] | null,
  screenerQualified?: string | string[] | null
): string[] {
  const cdpQualifiedProtocols = getQualifiedProtocols(cdpQualified);
  if (cdpQualifiedProtocols.length > 0) {
    return cdpQualifiedProtocols;
  }
  return getQualifiedProtocols(screenerQualified);
}

/**
 * UC-20/23: all flags OFF on no-site — skip API vs write Cancelled.
 * Outcome (NSA/NSC/…): always write Cancelled when not PWR so prior Initiated is cleared (abandon clock).
 * Contact submit: skip (no Initiated expected for that WF-1 no-site path).
 */
function shouldSkipPwrApiWhenAllFlagsOffOnNonReferral(options: {
  journeyType?: string | null;
  evaluationContext: PwrEvaluationContext;
  patientReferralStatus?: string | null;
  isSendToPWR: boolean;
  isReferralPathWithSite: boolean;
}): boolean {
  const { evaluationContext, patientReferralStatus, isSendToPWR, isReferralPathWithSite } = options;

  if (isSendToPWR || isReferralPathWithSite) {
    return false;
  }
  if (!isNoSiteReferralOutcome(patientReferralStatus)) {
    return false;
  }

  if (evaluationContext === 'contactSubmit') {
    return true;
  }

  // outcomePage / siteSelection — write Cancelled for NSA and NSC (do not leave Initiated).
  return false;
}

function buildPwrFlagsAuditPayload(
  protocolFlags: PWRFlagsResponse | null,
  inflareValue: string | null | undefined,
  isReferralPathWithSite: boolean
) {
  return {
    isFlareWatchEnabled: protocolFlags?.enableFlareWatch ?? false,
    isUserInFlare: isUserInFlare(inflareValue) ? 'true' : 'false',
    isPreRecruitmentEnabled: isPreRecruitmentOnForRouting(protocolFlags, isReferralPathWithSite),
    isSecondaryScreenerEnabled: protocolFlags?.enableSecondaryScreener ?? false,
  };
}
